import { RefObject, useRef } from "react"
import { Autoplay, Navigation, Pagination, Scrollbar, Mousewheel, SwiperOptions } from "swiper"
import { SwiperModule } from "swiper/types/shared"

/**
 * Abstracts away logic related to determining modules and normalising settings for
 * Swiper instance.
 *
 * @param settings Carousel settings object
 * @param optionsOverride An object containing optional Swiper configuration overrides
 */
const useCarousel = (settings?: GatsbyTypes.SanityCarouselSettings, optionsOverride?: SwiperOptions) => {
  const prevSlideRef = useRef<HTMLButtonElement>(null)
  const nextSlideRef = useRef<HTMLButtonElement>(null)

  // Retrieve enabled modules, along with normalised settings object
  const modules = settings ? getEnabledModules(settings) : []
  const normalisedSettings = settings ? getNormalisedSettings(settings, prevSlideRef, nextSlideRef) : {}

  return { settings: { ...normalisedSettings, ...optionsOverride, modules }, prevSlideRef, nextSlideRef }
}

/**
 * Returns a list of all enabled Swiper modules, depending on settings
 * object provided as an argument.
 *
 * @param settings Settings object sourced from Carousel
 */
const getEnabledModules = (settings: GatsbyTypes.SanityCarouselSettings): SwiperModule[] => {
  const modules = []

  if (settings.enableAutoplay) modules.push(Autoplay)
  if (settings.enablePagination) modules.push(Pagination)
  if (settings.enableNavigation) modules.push(Navigation)
  if (settings.enableScrollbar) modules.push(Scrollbar, Mousewheel)

  return modules
}

/**
 * Returns a normalised settings object matching the structure that is
 * expected by Swiper.
 *
 * @param settings Settings object sourced from Carousel
 * @param prevSlideRef React ref pointing to previous slide button
 * @param nextSlideRef React ref pointing to next slide button
 */
const getNormalisedSettings = (
  settings: GatsbyTypes.SanityCarouselSettings,
  prevSlideRef: RefObject<HTMLButtonElement>,
  nextSlideRef: RefObject<HTMLButtonElement>
): SwiperOptions => {
  const { enableAutoplay, slideDuration, enablePagination, enableNavigation, enableScrollbar, enableLoop } = settings

  const autoPlaySettings: SwiperOptions["autoplay"] = enableAutoplay
    ? { delay: (slideDuration || 1) * 1_000, disableOnInteraction: true }
    : false

  const paginationSettings: SwiperOptions["pagination"] = enablePagination ? { clickable: true } : false

  const navigationSettings: SwiperOptions["navigation"] = enableNavigation
    ? { prevEl: prevSlideRef?.current, nextEl: nextSlideRef?.current }
    : false

  const scrollbarSettings: SwiperOptions["scrollbar"] = enableScrollbar ? { enabled: true, draggable: true } : false
  const mousewheelSettings: SwiperOptions["mousewheel"] = enableScrollbar ? { sensitivity: 1, forceToAxis: true } : false

  return {
    direction: "horizontal",
    autoplay: autoPlaySettings,
    pagination: paginationSettings,
    navigation: navigationSettings,
    scrollbar: scrollbarSettings,
    mousewheel: mousewheelSettings,
    loop: (enableAutoplay && enableLoop) || false,
  }
}

export default useCarousel
